import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import 'moment/locale/es';
import 'moment/locale/pt';
import 'moment/locale/pt-br';
import { Router } from 'react-router-dom';
import { Provider } from 'react-redux';
import { createHashHistory as createHistory } from 'history';
import Bugsnag from '@bugsnag/js';
import configureStore from './redux/configureStore';
import { readStoredState, withFreshContext, signedOutStart, isStartUnreachable } from './redux/initialState';
import { Api, Log } from './helpers';
import {
  errorMessage as messageOf, afterVisibleTime, monotonicNow, hasMonotonicClock,
} from './helpers/appRecovery';
import initializeDBEngine from './redux/middleware/idbEngine';
import { listenForWebCheckoutReturn } from './redux/modules/context/subscription';
import initializeI18next from './i18n';
import './SocialSharing';
import { App, RefreshData } from './containers';
import Loading from './components/elements/Loading/Loading';
import ErrorBoundaryView from './components/blocks/ErrorBoundaryView/ErrorBoundaryView';
import CrashScreen from './components/blocks/ErrorBoundaryView/CrashScreen';
import StartUnreachable from './components/blocks/ErrorBoundaryView/StartUnreachable';
import { withPageTransitions } from './helpers/pageTransitions';
import { router } from './routes/router';

const createNotifiableError = (name, message) => ({ name, message });
// A start still on its spinner after this long with the page in front (a storage promise that never settles,
// as iOS can leave IndexedDB after the background) offers "Recarregar". It still opens if the start ends
// later. Time with the page hidden doesn't count: nobody was waiting. The API's part has its own wait
// (START_SLOW_AFTER and the "can't reach" screen).
export const START_TIMEOUT = 30 * 1000;
// The first start of a new version waiting this long (page in front) for the user and the clinics says it
// can't reach the API, with "Tentar de novo", and keeps waiting: a slow answer still opens the app
export const START_SLOW_AFTER = 15 * 1000;
// After the 1st, 2nd, 3rd… failed try in a row, the next one this long after (page in front). The page
// coming back to the front tries again once that long has passed; the connection coming back, at once.
export const START_RETRY_DELAYS = [10 * 1000, 20 * 1000, 40 * 1000, 60 * 1000];
// ±20%, so the clients a failure left on the screen don't all try again at the same moment
const START_RETRY_JITTER = 0.2;
// window and document can both say the connection is back (the native apps' plugin fires on document)
const ONLINE_DEDUPE_MS = 2000;

// When to try again after `failures` failed tries in a row: the backoff with jitter, and never sooner than
// the API's Retry-After
export const startRetryDelay = (failures: number, retryAfterMs: ?number, random: () => number = Math.random) => {
  const base = START_RETRY_DELAYS[Math.min(Math.max(failures, 1), START_RETRY_DELAYS.length) - 1];
  const delay = Math.round(base * (1 - START_RETRY_JITTER + 2 * START_RETRY_JITTER * random()));
  return Math.max(delay, retryAfterMs || 0);
};

// Where the start is, for Bugsnag: the step it waits for, the requests on their way, whether anyone was looking
const startMetadata = ({ step, pending, attempt }) => ({
  step,
  pending: pending.join(',') || null,
  attempt,
  visibility: document.visibilityState,
  online: window.navigator.onLine,
  sincePageLoadMs: hasMonotonicClock() ? Math.round(window.performance.now()) : null,
});

const AppEntryPoint = () => {
  const [store, setStore] = useState(null);
  const [appCrashMessage, setAppCrashMessage] = useState(null);
  // 'notStarted' | 'starting' | 'loadingContext' | 'step1Ready' | 'startingStep2' | 'ready' | 'crashed'
  const [appStartStep, setAppStartStep] = useState('notStarted');
  const [startStalled, setStartStalled] = useState(false);
  // The API couldn't be reached for a new version's user and clinics: "Tentar de novo", until the app opens
  const [unreachable, setUnreachable] = useState(false);
  // Each try at the user and the clinics, and how the current one goes: 'pending' | 'slow' | 'failed'.
  // The ref answers event handlers at once.
  const [attempt, setAttempt] = useState(1);
  const [attemptStatus, setAttemptStatus] = useState('pending');
  const attemptStatusRef = useRef('pending');
  // The step the start is on ('i18n', 'db', 'storedState', 'context') and, for 'context', the requests on their way
  const progress = useRef({ step: 'notStarted', pending: [], attempt: 1 });
  // The stored state, read once: a new try only asks for the user and the clinics again
  const storedForContext = useRef(null);
  // Gives up on the try on its way ("Sair e entrar de novo", a new connection)
  const cancelTry = useRef(() => {});
  // Failed tries in a row; after the last one, when it failed (monotonicNow), the wait before the next one,
  // and the API's Retry-After deadline
  const failures = useRef(0);
  const lastFailure = useRef({ at: 0, delay: 0, notBefore: 0 });
  const lastOnline = useRef(-Infinity);
  // StartUnreachable, once per page load for each reason ('slow', 'failed')
  const reportedUnreachable = useRef({});
  const started = appStartStep === 'ready';

  const history = useMemo(() => createHistory(), []);
  // The router's own hash history, made when the app first renders its routes, as HashRouter did, with page
  // transitions on the phone. Sharing Redux's would make Redux pushes re-render the routes inside the dispatch
  // (the patient list then asked for patients before the clinic was known).
  const routerHistory = useRef(null);

  // The crash screen ends the start: no try runs behind it (the API's step, its "online" and backoff, stop)
  const crashed = useRef(false);
  const crash = (err) => {
    crashed.current = true;
    setAppCrashMessage(messageOf(err));
    setAppStartStep('crashed');
    Bugsnag.notify(createNotifiableError('StartError', messageOf(err)));
  };
  const setStatus = (status) => {
    attemptStatusRef.current = status;
    setAttemptStatus(status);
  };
  // A start that just dropped its login, on a page that needs one, opens on the login, set before the router's
  // history reads the hash: that page would mount AppLayout, whose requests, without a token, get 401s (and a
  // sign-out). A page open without a login (sign-up, "Esqueci minha senha") keeps its link, and so does a start
  // that never had a login.
  const open = useCallback((state, droppedLogin = false) => {
    if (droppedLogin && router.needsLogin(window.location.hash.replace(/^#/, ''))) {
      window.location.replace(`#${router.getRoutePath('login')}`);
    }
    setUnreachable(false);
    setStore(configureStore(state, history));
    Log.debug('Start Step 1 Ready...');
    setAppStartStep('step1Ready');
  }, [history]);
  // A new try in place of the current one: after it failed, or ("online") when the connection it used is gone
  const newTry = useCallback(() => {
    if (crashed.current) return;
    attemptStatusRef.current = 'pending';
    cancelTry.current();
    setAttempt((n) => n + 1);
  }, []);
  // "Tentar de novo" and the backoff: only after a failed try, never over one on its way, even a slow one
  const retry = useCallback(() => {
    if (attemptStatusRef.current === 'failed') newTry();
  }, [newTry]);
  // "Sair e entrar de novo": logout()'s local cleanup, the try on its way no longer counts, and the app opens on
  // the login. Anything that throws ends on the crash screen, not on a "Tentando…" that never ends.
  const signOut = () => {
    try {
      const state = signedOutStart(window.__INITIAL_STATE__);
      cancelTry.current();
      open(state, true);
    } catch (err) {
      cancelTry.current();
      crash(err);
    }
  };

  // Storage and translations: a start stuck there offers "Recarregar". From the API's step on, the slow
  // timer and the "can't reach" screen own the wait; a crash has its own screen.
  const watchingStall = !started && !appCrashMessage && appStartStep !== 'loadingContext';
  useEffect(() => {
    if (!watchingStall) return undefined;
    return afterVisibleTime(START_TIMEOUT, () => {
      setStartStalled(true);
      const metadata = startMetadata(progress.current);
      Bugsnag.notify(createNotifiableError('StartStalled', `Still starting after ${START_TIMEOUT / 1000}s`), (event) => {
        event.addMetadata('start', metadata);
      });
    });
  }, [watchingStall]);


  useEffect(() => {
    (async () => {
      if (appStartStep === 'notStarted') {
        try {
          Log.debug('Runing App Start Step 1');
          setAppStartStep('starting');

          Log.debug('Initializing I18Next...');
          progress.current.step = 'i18n';
          await initializeI18next();
          Log.debug('Initializing DB Storage...');
          progress.current.step = 'db';
          await initializeDBEngine();
          Log.debug('Fetching initial state...');
          progress.current.step = 'storedState';
          const { state, needsContext } = await readStoredState(window.__INITIAL_STATE__);
          if (state) {
            open(state);
          } else {
            storedForContext.current = needsContext;
            setAppStartStep('loadingContext');
          }
        } catch (err) {
          crash(err);
        }
      }
    })();
  }, [appStartStep, open]);

  // A new version: the user and the clinics from the API. A try that can't reach the API leaves everything as
  // it was, login included, and shows "Tentar de novo". Only "Sair" and a new connection cancel a try.
  useEffect(() => {
    if (appStartStep !== 'loadingContext') return undefined;
    // Without AbortController (old WebViews) the requests can't be cut off, and their answer is ignored
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    let cancelled = false;
    let stopSlowTimer = () => {};
    const cancel = () => {
      cancelled = true;
      stopSlowTimer();
      if (controller) controller.abort();
    };
    cancelTry.current = cancel;
    progress.current = { step: 'context', pending: ['user', 'clinics'], attempt };
    setStatus('pending');

    const showUnreachable = (status, error) => {
      setStatus(status);
      setUnreachable(true);
      // Once per page load for each reason; offline, Bugsnag couldn't send it anyway
      if (reportedUnreachable.current[status] || window.navigator.onLine === false) return;
      reportedUnreachable.current[status] = true;
      const metadata = {
        ...startMetadata(progress.current),
        reason: status,
        status: error?.response?.status || null,
        code: error?.code || null,
      };
      Bugsnag.notify(createNotifiableError('StartUnreachable', `Could not load the user and clinics (${status})`), (event) => {
        event.addMetadata('start', metadata);
      });
    };

    // Waiting this long for the API with the page in front: say so, and keep waiting
    stopSlowTimer = afterVisibleTime(START_SLOW_AFTER, () => {
      if (!cancelled) showUnreachable('slow');
    });
    const onProgress = (step, pending = []) => {
      if (!cancelled) progress.current = { step, pending, attempt };
    };

    withFreshContext(storedForContext.current, window.__INITIAL_STATE__, {
      signal: controller ? controller.signal : null,
      isCancelled: () => cancelled,
      onProgress,
    })
      .then((initialState) => {
        stopSlowTimer();
        if (cancelled) return;
        failures.current = 0;
        open(initialState, !initialState.auth?.token);
      })
      .catch((err) => {
        stopSlowTimer();
        if (cancelled) return;
        if (isStartUnreachable(err)) {
          failures.current += 1;
          const at = monotonicNow();
          lastFailure.current = {
            at,
            delay: startRetryDelay(failures.current, err.retryAfterMs),
            notBefore: at + (err.retryAfterMs || 0),
          };
          showUnreachable('failed', err);
        } else {
          crash(err);
        }
      });

    return cancel;
  }, [appStartStep, attempt, open]);

  // The connection coming back (window, and document from cordova-plugin-network-information, as Toast.js)
  // means the old request's connection is likely gone: a new try, also in place of one on its way. Once per
  // change (both may fire), and never before the API's Retry-After.
  useEffect(() => {
    if (appStartStep !== 'loadingContext') return undefined;
    const onOnline = () => {
      const now = monotonicNow();
      if (now - lastOnline.current < ONLINE_DEDUPE_MS) return;
      if (attemptStatusRef.current === 'failed' && now < lastFailure.current.notBefore) return;
      lastOnline.current = now;
      newTry();
    };
    window.addEventListener('online', onOnline);
    document.addEventListener('online', onOnline);
    return () => {
      window.removeEventListener('online', onOnline);
      document.removeEventListener('online', onOnline);
    };
  }, [appStartStep, newTry]);

  // After a failed try, with "Tentar de novo" up: another one after the backoff with the page in front, or as
  // the page comes back to the front if that long has passed (a tab left in the background meanwhile)
  useEffect(() => {
    if (!unreachable || attemptStatus !== 'failed' || appCrashMessage) return undefined;
    const { at, delay, notBefore } = lastFailure.current;
    const onVisibilityChange = () => {
      const now = monotonicNow();
      if (document.visibilityState === 'visible' && now - at >= delay && now >= notBefore) retry();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    const stopTimer = afterVisibleTime(delay, retry);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      stopTimer();
    };
  }, [unreachable, attemptStatus, appCrashMessage, retry]);

  useEffect(() => {
    try {
      if (appStartStep === 'step1Ready') {
        Log.debug('Runing App Start Step 2');
        setAppStartStep('startingStep2');

        // Initialize API Context
        Api.initialize(store);

        listenForWebCheckoutReturn(store.dispatch);

        if (window.calabash) {
          window.calabash.start(() => {
            Log.debug('Calabash Server started');
          }, () => {
            Log.debug('Error starting Calabash Server');
          });
        }

        setAppStartStep('ready');
        Log.debug('App Start Steps Completed...');
      }
    } catch (err) {
      setAppCrashMessage(messageOf(err));
      Bugsnag.notify(createNotifiableError('StartError', messageOf(err)));
    }
  }, [store, appStartStep]);


  if (appCrashMessage) {
    return <CrashScreen title="O Doutore não abriu direito" message={appCrashMessage} />;
  }

  if (!store || !started) {
    if (unreachable) {
      return <StartUnreachable trying={attemptStatus !== 'failed'} onRetry={retry} onSignOut={signOut} />;
    }
    if (startStalled) return <CrashScreen title="O Doutore não abriu direito" />;
    return <Loading className="fullScreen"/>;
  }

  if (!routerHistory.current) routerHistory.current = withPageTransitions(createHistory());

  return (
    <ErrorBoundaryView>
      <Provider store={store}>
        <Router history={routerHistory.current}>
          <RefreshData />
          <App store={store} />
        </Router>
      </Provider>
    </ErrorBoundaryView>
  );
};


export default AppEntryPoint;
